import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, type PageMeta, type RequestOptions } from "./client";

export type Query = Record<string, string | number | boolean | undefined>;

export interface ListState<T> {
  data: T[];
  total: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Paged list from an envelope endpoint; refetches when `path`/`query` change. */
export function useApiList<T>(path: string, query: Query = {}, enabled = true): ListState<T> {
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const key = JSON.stringify(query);
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const mine = ++seq.current;
    setLoading(true);
    api<T[]>(path, { query: JSON.parse(key) as Query })
      .then(({ data, meta }) => {
        if (mine !== seq.current) return;
        setData(data ?? []);
        setTotal((meta as PageMeta | undefined)?.total ?? data?.length ?? 0);
        setError(null);
      })
      .catch((e: unknown) => {
        if (mine !== seq.current) return;
        setError(messageOf(e));
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  }, [path, key, enabled, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, total, loading, error, reload };
}

export interface OneState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (d: T) => void;
}

export function useApiOne<T>(path: string | null, query: Query = {}): OneState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const key = JSON.stringify(query);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    setLoading(true);
    api<T>(path, { query: JSON.parse(key) as Query })
      .then(({ data }) => {
        if (!cancelled) {
          setData(data);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(messageOf(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, key, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, loading, error, reload, setData };
}

/** Run a mutation with busy state and a friendly error string. */
export function useMutation() {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T,>(path: string, opts: RequestOptions): Promise<{ ok: true; data: T } | { ok: false; error: string }> => {
    setBusy(true);
    try {
      const { data } = await api<T>(path, opts);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: messageOf(e) };
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, run };
}

export function messageOf(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "validation_failed" && e.details && typeof e.details === "object") {
      const first = Object.entries(e.details as Record<string, unknown>)[0];
      if (first) return `${first[0]}: ${Array.isArray(first[1]) ? String(first[1][0]) : String(first[1])}`;
    }
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return "Something went wrong";
}
